export const REALTIME_VOICE_OPTIONS = [
  { value: "marin", provider: "openai", voiceId: "marin", label: "Marin 女声", gender: "female" },
  { value: "coral", provider: "openai", voiceId: "coral", label: "Coral 女声", gender: "female" },
  { value: "shimmer", provider: "openai", voiceId: "shimmer", label: "Shimmer 女声", gender: "female" },
  { value: "xai-ara", provider: "xai", voiceId: "ara", label: "Ara 女声 (xAI)", gender: "female" },
  { value: "xai-eve", provider: "xai", voiceId: "eve", label: "Eve 女声 (xAI)", gender: "female" },
  {
    value: "aliyun-qwen3-tts-flash",
    provider: "aliyun",
    voiceId: "Cherry",
    label: "Qwen3 TTS Flash 女声 (阿里)",
    gender: "female"
  },
  {
    value: "aliyun-qwen-audio-3-plus",
    provider: "qwen-audio",
    voiceId: "longanlingxin",
    modelId: "qwen-audio-3.0-realtime-plus",
    label: "Qwen Audio 3.0 Plus · 灵心女声 (阿里)",
    gender: "female"
  },
  {
    value: "aliyun-qwen-audio-3-flash",
    provider: "qwen-audio",
    voiceId: "longanlingxin",
    modelId: "qwen-audio-3.0-realtime-flash",
    label: "Qwen Audio 3.0 Flash · 灵心女声 (阿里)",
    gender: "female"
  },
  {
    value: "minicpm-o-45",
    provider: "minicpm",
    voiceId: "minicpm-o-45",
    label: "MiniCPM-o 4.5 女声",
    gender: "female"
  }
] as const;

export const REALTIME_VOICES = REALTIME_VOICE_OPTIONS.map((voice) => voice.value);

export type RealtimeVoice = (typeof REALTIME_VOICES)[number];
export type RealtimeVoiceProvider = (typeof REALTIME_VOICE_OPTIONS)[number]["provider"];
export type RealtimeRoute = RealtimeVoiceProvider;

export const DEFAULT_REALTIME_VOICE: RealtimeVoice = "xai-ara";
export const DEFAULT_REALTIME_ROUTE: RealtimeRoute = "xai";

export const REALTIME_ROUTE_OPTIONS = [
  {
    value: "xai",
    label: "xAI Voice Agent",
    headline: "最快进入角色通话",
    description: "Grok Voice 直接负责听、想和说，延迟低，表演感强，适合体验默认剧情节奏。",
    badge: "默认"
  },
  {
    value: "openai",
    label: "OpenAI Realtime",
    headline: "工具调用和多模态编排更稳",
    description: "GPT Realtime 负责语音对话和工具调用，适合测试剧情控制、场景图联动和可控性。",
    badge: "实验"
  },
  {
    value: "qwen-audio",
    label: "Qwen Audio 3.0",
    headline: "端到端实时语音对话",
    description: "阿里 Qwen Audio 直接负责听、想和说，Plus 默认重表现力，Flash 可切换为低延迟档。",
    badge: "全双工"
  },
  {
    value: "aliyun",
    label: "阿里拆分链路",
    headline: "ASR / 文本大脑 / TTS 分层",
    description: "Fun-ASR + GPT Brain + Qwen TTS 分开运行，台词文本更明确，适合排查节奏和字幕一致性。",
    badge: "可控"
  },
  {
    value: "minicpm",
    label: "MiniCPM-o 4.5",
    headline: "开源多模态实时语音",
    description: "接入 MiniCPM-o 官方新版 /v1/realtime?mode=audio，按住时流式送入语音、松开后补静音触发回应；文本补声只使用 MiniCPM 自身 TTS。",
    badge: "开源"
  }
] as const satisfies ReadonlyArray<{
  value: RealtimeRoute;
  label: string;
  headline: string;
  description: string;
  badge: string;
}>;

const REALTIME_VOICE_SET = new Set<string>(REALTIME_VOICES);
const REALTIME_VOICE_ALIAS = new Map<string, RealtimeVoice>([
  ["ara", "xai-ara"],
  ["eve", "xai-eve"]
]);

export function isRealtimeVoice(value: unknown): value is RealtimeVoice {
  return typeof value === "string" && REALTIME_VOICE_SET.has(value);
}

export function resolveRealtimeVoice(value: unknown, fallback: RealtimeVoice = DEFAULT_REALTIME_VOICE): RealtimeVoice {
  if (typeof value === "string") {
    const alias = REALTIME_VOICE_ALIAS.get(value);
    if (alias) return alias;
  }
  return isRealtimeVoice(value) ? value : fallback;
}

export function getRealtimeVoiceConfig(value: RealtimeVoice) {
  return REALTIME_VOICE_OPTIONS.find((voice) => voice.value === value) ?? REALTIME_VOICE_OPTIONS[0];
}

export function getRealtimeRouteForVoice(value: RealtimeVoice): RealtimeRoute {
  return getRealtimeVoiceConfig(value).provider;
}

export function getRealtimeRouteConfig(value: RealtimeRoute) {
  return REALTIME_ROUTE_OPTIONS.find((route) => route.value === value) ?? REALTIME_ROUTE_OPTIONS[0];
}

export function getRealtimeVoicesForRoute(value: RealtimeRoute) {
  return REALTIME_VOICE_OPTIONS.filter((voice) => voice.provider === value);
}

export function resolveRealtimeRouteVoice(route: RealtimeRoute, voice: RealtimeVoice): RealtimeVoice {
  const voices = getRealtimeVoicesForRoute(route);
  return voices.some((option) => option.value === voice) ? voice : (voices[0]?.value ?? DEFAULT_REALTIME_VOICE);
}
